import { FORUM_POST_UPDATE_EVENT, LIMIT } from '@/constants';
import {
  fetchForumCustomer,
  fetchForumNotifications,
  fetchForumPostDetail,
  fetchForumPostDown,
  fetchForumPostUp,
  fetchForumReviews,
  fetchForumTag,
  fetchForumTags,
  fetchHotForumPosts,
  fetchHotForumPostsByActivity,
  fetchHotForumPostsByTag,
  fetchMyForumPosts,
  fetchNewForumPosts,
  fetchNewForumPostsByActivity,
  fetchNewForumPostsByTag,
  fetchUnAuditForumPosts,
} from '@/services';
import {
  ForumActivityStatus,
  ForumMainTabType,
  ForumNotificationType,
  ForumPostHotDateType,
  ForumPostStatus,
  LoadMoreStatus,
  Sort,
} from '@/types';
import { toast } from '@/utils';
import { defineStore } from 'pinia';

interface ForumState {
  /** 弹窗引用 */
  popupRef: {
    comment: AnyObject | null;
  };
  /** 发布选择提交 */
  publishSelectedSubmit: {
    topic: AnyObject | null;
    note: AnyObject | null;
    grass: AnyObject | null;
  };
  /** 话题列表数据配置 */
  topicsConfig: {
    list: AnyObject[];
    /** 列表加载状态 */
    loadStatus: LoadMoreStatus;
    /** 列表请求选项 */
    options: {
      skip: number;
    };
  };
  /** 话题详情 */
  topicDetail: AnyObject | null;
  /** 帖子列表数据配置 */
  notesConfig: {
    list: AnyObject[];
    /** 列表加载状态 */
    loadStatus: LoadMoreStatus;
    /** 列表请求选项 */
    options: {
      skip: number;
    };
  };
  /** 帖子详情 */
  noteDetail: AnyObject | null;
  /** 种草商品数据配置 */
  grassProductsConfig: {
    list: AnyObject[];
    loadStatus: LoadMoreStatus;
    options: {
      skip: number;
    };
  };
  /** 话题详情帖子列表（最热、最新）数据配置 */
  topicDetailNotesConfig: {
    list: AnyObject[];
    loadStatus: LoadMoreStatus;
    options: {
      skip: number;
    };
  };
  /** 用户信息数据配置 */
  profileNotesConfig: {
    list: AnyObject[];
    loadStatus: LoadMoreStatus;
    total: number;
    options: {
      skip: number;
      status: ForumPostStatus;
    };
  };
  /** 帖子通知数据配置 */
  notificationsConfig: {
    list: AnyObject[];
    loadStatus: LoadMoreStatus;
    options: {
      skip: number;
      notificationType: ForumNotificationType;
    };
  };
  /** 帖子评论数据配置 */
  commentNoteConfig: {
    list: AnyObject[];
    loadStatus: LoadMoreStatus;
    total: number;
    options: {
      skip: number;
      sort: {
        reviewTime?: Sort;
        upVotes?: Sort;
      };
    };
  };
  /** 活动排行榜数据配置 */
  activityRankingConfig: {
    list: AnyObject[];
    loadStatus: LoadMoreStatus;
    options: {
      skip: number;
      activityId: string;
      forumPostHotDateType: ForumPostHotDateType;
    };
  };
  /** 话题详情前三名 */
  topicDetailTopThreeRankList: AnyObject[];
  /** 编辑帖子 */
  editNoteData: AnyObject | null;
  /** 帖子作者信息 */
  forumCustomer: AnyObject | null;
  /** 切换tab锁 */
  switchLock: boolean;
}

export const useForumStore = defineStore('forum', {
  state: (): ForumState => ({
    popupRef: {
      comment: null,
    },
    publishSelectedSubmit: {
      topic: null,
      note: null,
      grass: null,
    },
    topicsConfig: {
      list: [],
      loadStatus: LoadMoreStatus.loading,
      options: {
        skip: 0,
      },
    },
    topicDetail: null,
    notesConfig: {
      list: [],
      loadStatus: LoadMoreStatus.loading,
      options: {
        skip: 0,
      },
    },
    noteDetail: null,
    grassProductsConfig: {
      list: [],
      loadStatus: LoadMoreStatus.loading,
      options: {
        skip: 0,
      },
    },
    topicDetailNotesConfig: {
      list: [],
      loadStatus: LoadMoreStatus.loading,
      options: {
        skip: 0,
      },
    },
    profileNotesConfig: {
      list: [],
      loadStatus: LoadMoreStatus.loading,
      total: 0,
      options: {
        skip: 0,
        status: ForumPostStatus.none,
      },
    },
    notificationsConfig: {
      list: [],
      loadStatus: LoadMoreStatus.loading,
      options: {
        skip: 0,
        notificationType: ForumNotificationType.reply,
      },
    },
    commentNoteConfig: {
      list: [],
      total: 0,
      loadStatus: LoadMoreStatus.loading,
      options: {
        skip: 0,
        sort: { reviewTime: Sort.DESC },
      },
    },
    activityRankingConfig: {
      list: [],
      loadStatus: LoadMoreStatus.loading,
      options: {
        skip: 0,
        activityId: '',
        forumPostHotDateType: ForumPostHotDateType.alltime,
      },
    },
    editNoteData: null,
    forumCustomer: null,
    switchLock: false,
    topicDetailTopThreeRankList: [],
  }),

  actions: {
    async getTopicsList({ skip = 0 }, refresh?: boolean) {
      const variables = {
        options: {
          skip,
        },
      };
      const res = await fetchForumTags(variables);
      if (res?.items) {
        if (res.items.length < LIMIT) {
          this.topicsConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.topicsConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.topicsConfig.list = res.items;
        } else {
          this.topicsConfig.list = [...this.topicsConfig.list, ...res.items];
        }
      }
      return this.topicsConfig.list;
    },

    async getTopicDetail(id: string, refresh?: boolean) {
      if (!this.topicDetail || refresh) {
        const res = await fetchForumTag(id);
        if (res) {
          this.topicDetail = res;
          console.log('this.topicDetail: ', this.topicDetail);
        }
      }
      return this.topicDetail;
    },

    async getForumPosts(
      type: ForumMainTabType,
      { skip = 0, forumPostHotDateType = 'alltime' },
      refresh?: boolean
    ) {
      const variables = {
        forumPostHotDateType,
        options: {
          skip,
        },
      };

      let res;
      if (type === ForumMainTabType.hot) {
        res = await fetchHotForumPosts(variables);
      } else {
        res = await fetchNewForumPosts(variables);
      }

      if (res?.items) {
        if (res.items.length < LIMIT) {
          this.notesConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.notesConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.notesConfig.list = res.items;
        } else {
          this.notesConfig.list = [...this.notesConfig.list, ...res.items];
        }
      }

      console.log(`this.notesConfig.list: `, this.notesConfig.list);

      return this.notesConfig.list;
    },

    async getTopicDetailNotes(
      type: ForumMainTabType,
      { forumTagId = '', skip = 0, forumPostHotDateType = 'alltime' },
      refresh?: boolean
    ) {
      const variables = {
        forumTagId,
        forumPostHotDateType,
        options: {
          skip,
        },
      };

      let res;
      if (type === ForumMainTabType.hot) {
        res = await fetchHotForumPostsByTag(variables);
      } else {
        res = await fetchNewForumPostsByTag(variables);
      }

      if (res?.items) {
        if (res.items.length < LIMIT) {
          this.topicDetailNotesConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.topicDetailNotesConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.topicDetailNotesConfig.list = res.items;
        } else {
          this.topicDetailNotesConfig.list = [...this.topicDetailNotesConfig.list, ...res.items];
        }
      }

      console.log(`this.topicDetailNotesConfig.list: `, this.topicDetailNotesConfig.list);

      return this.topicDetailNotesConfig.list;
    },

    async getProfileNotes(
      type: ForumMainTabType,
      { skip = 0, status = ForumPostStatus.none },
      refresh?: boolean
    ) {
      const variables = {
        options: {
          skip,
          status,
        },
      };

      let res;
      if (type === ForumMainTabType.all) {
        res = await fetchMyForumPosts(variables);
        this.profileNotesConfig.total = res?.totalItems ?? 0;
      } else if (type === ForumMainTabType.audit) {
        res = await fetchUnAuditForumPosts(variables);
      } else if (type === ForumMainTabType.draft) {
        res = await fetchUnAuditForumPosts(variables);
      }

      if (res?.items) {
        if (res.items.length < LIMIT) {
          this.profileNotesConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.profileNotesConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.profileNotesConfig.list = res.items;
        } else {
          this.profileNotesConfig.list = [...this.profileNotesConfig.list, ...res.items];
        }
      }

      console.log(`this.profileNotesConfig.list: `, this.profileNotesConfig.list);

      return this.profileNotesConfig.list;
    },

    async getNotifications(
      notificationType: ForumNotificationType,
      { skip = 0 },
      refresh?: boolean
    ) {
      const variables = {
        notificationType,
        options: {
          skip,
        },
      };

      const res = await fetchForumNotifications(variables);

      if (res?.items) {
        if (res.items.length < LIMIT) {
          this.notificationsConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.notificationsConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.notificationsConfig.list = res.items;
        } else {
          this.notificationsConfig.list = [...this.notificationsConfig.list, ...res.items];
        }
      }

      console.log(`this.notificationsConfig.list: `, this.notificationsConfig.list);

      return this.notificationsConfig.list;
    },

    async getNoteDetail(id: string, refresh?: boolean) {
      if (!this.noteDetail || refresh) {
        const res = await fetchForumPostDetail(id);
        res && (this.noteDetail = res);
      }
      return this.noteDetail;
    },

    async getNoteComments(
      pid: string,
      level: number,
      { skip = 0, sort = { reviewTime: Sort.DESC } }: { skip?: number; sort?: AnyObject },
      refresh?: boolean
    ) {
      const variables = {
        pid,
        level,
        options: {
          skip,
          sort,
        },
      };

      const res = await fetchForumReviews(variables);

      if (res?.items) {
        // 添加二级评论渲染字段
        res.items = res.items.map((item: AnyObject) => {
          if (!item?._items?.length) item._items = [];
          if (!item?._totalItems) {
            if (item?.reviewCount > 0) {
              item._totalItems = item.reviewCount;
            } else {
              item._totalItems = 0;
            }
          }
          return item;
        });

        this.commentNoteConfig.total = res.totalItems;

        if (res.items.length < LIMIT) {
          this.commentNoteConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.commentNoteConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.commentNoteConfig.list = res.items;
        } else {
          this.commentNoteConfig.list = [...this.commentNoteConfig.list, ...res.items];
        }
      }

      console.log(`this.commentNoteConfig.list: `, this.commentNoteConfig.list);

      return this.commentNoteConfig.list;
    },

    async getActivityRanking(
      type: ForumMainTabType,
      activityId: string,
      forumPostHotDateType: ForumPostHotDateType = ForumPostHotDateType.alltime,
      { skip = 0 },
      refresh?: boolean
    ) {
      const variables = {
        activityId,
        forumPostHotDateType,
        options: {
          skip,
        },
      };

      let res;
      if (type === ForumMainTabType.hot) {
        res = await fetchHotForumPostsByActivity(variables);

        // 话题详情前三名 - 只取最热tab
        this.topicDetailTopThreeRankList = res.items.slice(0, 3);
      } else {
        res = await fetchNewForumPostsByActivity(variables);
      }

      if (res?.items) {
        // 添加排名字段
        res.items = res.items.map((item: AnyObject, index: number) => {
          item._rank = index + 1;
          return item;
        });

        if (res.items.length < LIMIT) {
          this.activityRankingConfig.loadStatus = LoadMoreStatus.noMore;
        } else {
          this.activityRankingConfig.loadStatus = LoadMoreStatus.more;
        }
        if (refresh) {
          this.activityRankingConfig.list = res.items;
        } else {
          this.activityRankingConfig.list = [...this.activityRankingConfig.list, ...res.items];
        }
      }

      console.log(`this.activityRankingConfig.list: `, this.activityRankingConfig.list);

      return this.activityRankingConfig.list;
    },

    async getForumCustomer(refresh?: boolean) {
      if (!this.forumCustomer || refresh) {
        const res = await fetchForumCustomer();
        if (res) this.forumCustomer = res;
        console.log(`作者信息: `, this.forumCustomer);
      }
      return this.forumCustomer;
    },

    updateForumPost(data: AnyObject, list: AnyObject[]) {
      const index = list.findIndex((item: AnyObject) => item.id === data.id);
      if (index > -1) {
        list[index] = data;
      }
      return [...list];
    },

    setLevelFields(data: AnyObject) {
      if (!data?._items?.length) data._items = [];
      if (!data?._totalItems) data._totalItems = 0;
      return data;
    },

    getTopicActivityConfig(data: AnyObject | null) {
      const forumTagActivity = data?.forumTagActivity;

      if (!forumTagActivity) return null;

      const config = {
        isNormal: forumTagActivity.status === ForumActivityStatus.normal,
        isNotStarted: forumTagActivity.status === ForumActivityStatus.notStarted,
        isFailure: forumTagActivity.status === ForumActivityStatus.failure,
        isHaveEnded: forumTagActivity.status === ForumActivityStatus.haveEnded,
        countDownTime: 0,
        countDownTimePrefix: '',
        mainText: '',
        _source: data,
      };

      if (config.isNotStarted) {
        config.countDownTime = new Date(forumTagActivity.startTime).getTime();
        config.countDownTimePrefix = '后开始';
      } else if (config.isNormal) {
        config.countDownTime = new Date(forumTagActivity.endTime).getTime();
        config.countDownTimePrefix = '后结束';
      } else if (config.isFailure || config.isHaveEnded) {
        config.mainText = '活动已结束';
      }

      console.log(`getTopicActivityConfig: `, config);

      return config;
    },

    async fetchLikeHandler(id: string, isLiked: boolean) {
      let res;
      if (isLiked) {
        res = await fetchForumPostDown(id);
      } else {
        res = await fetchForumPostUp(id);
      }
      return res;
    },

    async handleNoteDetailLike() {
      if (!this.noteDetail) return;

      const cancelLoading = toast.loading();

      const res = await this.fetchLikeHandler(
        this.noteDetail?.nodeBBPostId,
        this.noteDetail?.upVoted
      );

      if (res) {
        this.noteDetail.upVoted = res.upVoted;
        this.noteDetail.upVotes = res.upVoted
          ? this.noteDetail.upVotes + 1
          : this.noteDetail.upVotes - 1;
      }

      uni.$emit(FORUM_POST_UPDATE_EVENT, this.noteDetail);

      cancelLoading();
    },

    resetPublishSelectedSubmit() {
      this.publishSelectedSubmit = {
        topic: null,
        note: null,
        grass: null,
      };
    },
  },
});
